import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Calendar,
  MapPin,
  Trophy,
  Edit,
  ArrowLeft,
  Clock,
  Upload,
  Users,
  LayoutGrid,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { getEvent } from "../../graphql/queries";
import { updateEvent } from "../../graphql/mutations";
import { formatDate } from "../../utils/dateUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DeleteEventButton from "../../components/DeleteEventButton";

const client = generateClient();

const EventDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Mutation to update event completion status
  const updateEventMutation = useMutation({
    mutationFn: async ({
      id,
      isCompleted,
    }: {
      id: string;
      isCompleted: boolean;
    }) => {
      const result = await client.graphql({
        query: updateEvent,
        variables: {
          input: {
            id,
            isCompleted,
          },
        },
      });
      return result.data.updateEvent;
    },
    onSuccess: () => {
      // Invalidate and refetch the event data
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["leagueEvents"] });
    },
  });

  const getStatusBadge = (isCompleted: boolean) => {
    if (isCompleted) {
      return <Badge variant="default">Completed</Badge>;
    }
    return <Badge variant="secondary">In Progress</Badge>;
  };

  const toggleEventCompletion = () => {
    if (event) {
      updateEventMutation.mutate({
        id: event.id,
        isCompleted: !event.isCompleted,
      });
    }
  };

  if (eventLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading event: {eventError.message}
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">Event not found</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/leagues/${event.leagueId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to League
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{event.name}</h1>
          <p className="text-muted-foreground mt-2">
            Event details and management
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/events/${eventId}/registration`)}
          >
            <Users className="h-4 w-4 mr-2" />
            Registration
          </Button>
          {event.format === "Commander" && (
            <Button
              onClick={() =>
                navigate(`/admin/events/${eventId}/commander-rounds`)
              }
            >
              <LayoutGrid className="h-4 w-4 mr-2" />
              Commander Pods
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/events/${eventId}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Event
          </Button>
          <Button
            onClick={() => navigate(`/admin/events/${eventId}/standings`)}
          >
            <Trophy className="h-4 w-4 mr-2" />
            Manage Standings
          </Button>
          {event?.hasTopCut && (
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/events/${eventId}/topcut`)}
            >
              <Trophy className="h-4 w-4 mr-2" />
              Top Cut Results
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/events/${eventId}/results/import`)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import EventLink Data (New System)
          </Button>
          <DeleteEventButton
            eventId={eventId!}
            eventName={event.name}
            onDeleted={() => navigate(`/admin/leagues/${event.leagueId}`)}
          />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Event Details */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
              <CardDescription>Basic event information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Status</span>
                <div className="flex items-center gap-2">
                  {getStatusBadge(event.isCompleted)}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleEventCompletion}
                    disabled={updateEventMutation.isPending}
                  >
                    {updateEventMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    ) : event.isCompleted ? (
                      "Mark In Progress"
                    ) : (
                      "Mark Completed"
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(event.date)}</span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4" />
                  <span>{event.location}</span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Trophy className="h-4 w-4" />
                  <span>{event.format}</span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>Max Rounds: {event.maxRounds}</span>
                </div>
              </div>

              {event.description && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground">
                    {event.description}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* EventLink Integration */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                EventLink Integration
              </CardTitle>
              <CardDescription>
                Import tournament results from EventLink
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-8">
                <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Import EventLink Data
                </h3>
                <p className="text-muted-foreground mb-4">
                  Paste EventLink PDF standings data to import tournament
                  results
                </p>
                <Button
                  onClick={() =>
                    navigate(`/admin/events/${eventId}/results/import`)
                  }
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import EventLink Data (New System)
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
